import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Sparkles,
  Loader2,
  CheckCircle2,
  Headphones,
  Music2,
  Waves,
  Sun,
  HeartPulse,
  Eye,
  Mountain,
  Drum,
} from "lucide-react";
import { Container, FadeIn, SectionDivider } from "@/components/Section";
import PageHero from "@/components/PageHero";
import { IMG } from "@/lib/data";
import MultiInterestSelector from "@/components/MultiInterestSelector";
import ReferralSourceSelector from "@/components/ReferralSourceSelector";
import NextStepsBlock from "@/components/NextStepsBlock";
import AcuityScheduleBlock from "@/components/AcuityScheduleBlock";
import PartnersStrip from "@/components/PartnersStrip";
import PaymentMethodsBadge from "@/components/PaymentMethodsBadge";
import SEOHead from "@/components/SEOHead";
import StayConnected from "@/components/StayConnected";
import { pageSource, deviceSource } from "@/lib/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ELEMENTS = [
  { Icon: Headphones, title: "Mindfulness VR", desc: "Immersive guided journeys through sound and light, headset-led." },
  { Icon: Drum, title: "Live drums & steel pans", desc: "Caribbean and West African ancestral instruments played live." },
  { Icon: Music2, title: "432 Hz frequencies", desc: "Tuned soundscapes designed for nervous-system regulation." },
  { Icon: Waves, title: "Sound bowls & vibration", desc: "Brass and crystal bowls layered into the journey." },
  { Icon: Eye, title: "Guided visualisation", desc: "Cinematic visualisation rooted in diasporic story." },
  { Icon: HeartPulse, title: "Breathwork", desc: "Pranayama, holotropic and ancestral breath techniques." },
  { Icon: Mountain, title: "Yoga & movement", desc: "Slow, embodied yoga inside projection-mapped chambers." },
  { Icon: Sun, title: "Ambient immersive lighting", desc: "Sunrise-warmth lighting design synced to the soundscape." },
];

const AUDIENCES = [
  { value: "individual", label: "Individual" },
  { value: "couple", label: "Couple" },
  { value: "family", label: "Family" },
  { value: "corporate", label: "Corporate wellbeing" },
  { value: "nhs-wellbeing", label: "NHS / wellbeing referral" },
  { value: "council-partner", label: "Council partnership" },
  { value: "community-org", label: "Community organisation" },
  { value: "waitlist", label: "Just the waitlist" },
];

const INTEREST_OPTIONS = ELEMENTS.map((e) => e.title).concat([
  "Mindfulness VR experience",
  "Collective healing circle",
  "Ancestral sound ceremony",
  "Family wellness session",
]);

const PILLARS = [
  {
    title: "Mental wellbeing rooted in culture",
    desc: "Wellness designed by and for the global majority — not adapted from elsewhere.",
  },
  {
    title: "Mindfulness through immersive arts",
    desc: "Projection, sound, light and movement become the meditation, not a distraction from it.",
  },
  {
    title: "Sensory healing environments",
    desc: "Chambers tuned by sound, scent and visual frequency for nervous-system rest.",
  },
  {
    title: "Reconnection through sound, movement & visualisation",
    desc: "Live drums and steel pans, slow yoga, guided visualisation — collective and personal.",
  },
  {
    title: "Technology + ancestral wellness",
    desc: "VR mindfulness journeys sit beside hand-carved instruments and 432 Hz frequencies.",
  },
  {
    title: "Ubuntu philosophy",
    desc: "I am because we are. Each retreat is held as a circle, not a class.",
  },
];

export default function UbuntuRetreat() {
  return (
    <div className="min-h-screen bg-[#050505]">
      <SEOHead
        cmsPage="ubuntu-retreat"
        title="Ubuntu Immersive Retreat · Mindfulness VR · Sound Healing · TOK"
        description="A monthly premium immersive wellness ceremony blending Mindfulness VR, live drums, steel pans, 432 Hz frequencies, sound bowls, breathwork and yoga inside an Afrofuturist sanctuary. Medway · London · Kent. Family, corporate and NHS partnerships welcome."
        image={IMG.ubuntuRetreat}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Event",
          name: "Ubuntu Immersive Retreat",
          eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
          eventStatus: "https://schema.org/EventScheduled",
          description: "Monthly immersive wellness ceremony blending mindfulness VR, ancestral sound, live drums, steel pans, 432 Hz frequencies, breathwork and yoga inside an Afrofuturist sanctuary rooted in Ubuntu philosophy.",
          image: IMG.ubuntuRetreat,
          organizer: { "@type": "Organization", name: "Temple of Kulture" },
          location: {
            "@type": "Place",
            name: "Temple of Kulture · Medway",
            address: { "@type": "PostalAddress", addressLocality: "Medway", addressRegion: "Kent", addressCountry: "GB" },
          },
        }}
      />
      <PageHero
        cmsPage="ubuntu-retreat"
        eyebrow="Once a month · 12 a year"
        title="Ubuntu Immersive Retreat"
        subtitle="A monthly premium wellness ceremony blending mindfulness VR, ancestral sound, live drums, steel pans, 432 Hz frequencies, breathwork and yoga inside an Afrofuturist sanctuary. Held as a circle, not a class."
        image={IMG.ubuntuRetreat}
      />

      {/* Atmosphere intro */}
      <section className="py-16 sm:py-20 border-y border-amber-500/10 bg-black">
        <Container className="grid lg:grid-cols-3 gap-8">
          <InfoCard
            label="Frequency"
            title="Once a month"
            body="Twelve retreats per year. Saturdays and Sundays alternating. Held at TOK Medway and partner sanctuaries across Kent and London."
          />
          <InfoCard
            label="Length"
            title="Half-day · evening"
            body="Three-hour ceremony with extended openings for corporate wellbeing days and family wellness afternoons."
          />
          <InfoCard
            label="Investment"
            title="Paid · premium"
            body="Per-seat tickets, membership pricing and bespoke packages for NHS/wellbeing/community partners. Funded places periodically available."
          >
            <PaymentMethodsBadge />
          </InfoCard>
        </Container>
      </section>

      {/* Twelve elements grid */}
      <SectionDivider label="Inside the chamber" />
      <Container className="pb-16">
        <div className="max-w-2xl mb-10">
          <p className="tok-eyebrow">Inside the chamber</p>
          <h2 className="tok-headline-xl mt-3">
            Eight elements <br />
            woven into <span className="text-amber-400">one ceremony.</span>
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {ELEMENTS.map((e, i) => (
            <FadeIn key={e.title} delay={i * 0.04}>
              <div
                data-testid={`ubuntu-element-${i}`}
                className="border border-white/5 bg-zinc-950 p-6 h-full hover:border-amber-400/30 transition-colors"
              >
                <e.Icon className="h-5 w-5 text-amber-400" />
                <h3 className="tok-headline-md mt-4 text-base">{e.title}</h3>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
                  {e.desc}
                </p>
              </div>
            </FadeIn>
          ))}
        </div>
      </Container>

      {/* What you'll take away */}
      <SectionDivider label="What you'll take with you" />
      <Container className="pb-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {PILLARS.map((p) => (
            <div
              key={p.title}
              className="border border-white/5 bg-black p-6"
              data-testid={`ubuntu-pillar-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <h3 className="tok-headline-md text-lg">{p.title}</h3>
              <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
                {p.desc}
              </p>
            </div>
          ))}
        </div>
      </Container>

      {/* Booking / register interest */}
      <SectionDivider label="Reserve · enquire · waitlist" />
      <Container className="pb-20 max-w-5xl">
        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3">
            <UbuntuForm />
          </div>
          <div className="lg:col-span-2 space-y-5">
            <AcuityScheduleBlock
              eyebrow="Before you book"
              title="Discovery call with the retreat lead"
              copy="A 20-minute call to talk through wellness needs, accessibility, group bookings or NHS / corporate partnerships."
              slug="ubuntu-retreat"
              audience="Adults"
              duration="20 mins"
              fallbackTo="#register-interest"
              fallbackLabel="Open the form"
              testId="acuity-ubuntu-discovery"
            />
            <div className="border border-white/5 bg-zinc-950 p-6 text-xs text-zinc-400 leading-relaxed">
              <p className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold">
                What&apos;s the difference?
              </p>
              <p className="mt-3">
                <strong className="text-zinc-200">Scheduled programmes</strong>{" "}
                (Acuity) — taster sessions, one-to-one calls, retreat slots
                that need a specific time.
              </p>
              <p className="mt-3">
                <strong className="text-zinc-200">Ticketed events</strong>{" "}
                (Stripe / Eventbrite / Fatsoma) — anime socials, brunches,
                festivals, headline shows.
              </p>
            </div>
          </div>
        </div>
      </Container>

      {/* Conversion next-steps */}
      <NextStepsBlock
        title="Stay deeper inside the Temple"
        steps={[
          { label: "Programme", title: "LEVEL UP", desc: "Our 16–25 immersive arts pathway.", to: "/level-up-xr", cta: "Discover →", testid: "level-up" },
          { label: "Sign up", title: "Join the Temple", desc: "Early access to every retreat, show and experience.", to: "/join", cta: "Join →", testid: "join" },
          { label: "Partner", title: "NHS / wellbeing partnerships", desc: "Bring Ubuntu to your service, school or council.", to: "/community#partnerships", cta: "Partner with us →", testid: "partner" },
        ]}
      />

      {/* Ubuntu Retreat partners */}
      <section className="py-20 sm:py-24 bg-zinc-950 border-t border-amber-500/10">
        <PartnersStrip
          eyebrow="Supported by"
          title="Real venues, real councils, real wellbeing partners"
          copy="Ubuntu Retreats happen in real, beautifully-held spaces with funders and councils that share our commitment to global majority wellbeing."
          programmeKey="ubuntu"
          variant="card"
          testId="ubuntu-partners"
        />
      </section>

      {/* WhatsApp opt-in — monthly retreat announcements */}
      <StayConnected
        variant="whatsapp"
        pageSource="ubuntu-retreat"
        testid="ubuntu-stay"
        whatsappHeadline="MONTHLY RETREAT ANNOUNCEMENTS"
        whatsappTagline="Next retreat dates, early-bird windows and behind-the-scenes from the held space — direct on WhatsApp."
        whatsappCta="Join the Channel"
        bullets={[
          "Monthly retreat dates & venues",
          "Early-bird booking windows",
          "Practitioner lineups",
          "Late-release seats and waitlist openings",
          "Mid-month wellbeing rituals & gatherings",
        ]}
      />
    </div>
  );
}

function InfoCard({ label, title, body, children }) {
  return (
    <div className="border border-white/5 p-7">
      <p className="tok-eyebrow-sm">{label}</p>
      <h3 className="tok-headline-md mt-2 text-2xl">{title}</h3>
      <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{body}</p>
      {children}
    </div>
  );
}

function UbuntuForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    audience_type: "individual",
    preferred_month: "",
    party_size: 1,
    interests: [],
    accessibility_notes: "",
    message: "",
    referral_source: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => {
    const val =
      e.target.type === "number" ? Number(e.target.value || 0) : e.target.value;
    setForm((f) => ({ ...f, [k]: val }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required.");
      return;
    }
    if (!form.referral_source) {
      toast.error("Please tell us how you heard about TOK.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/retreat-bookings`, {
        ...form,
        party_size: Math.max(1, Number(form.party_size) || 1),
        page_source: pageSource(),
        device_source: deviceSource(),
      });
      setDone(true);
      toast.success("Enquiry received — Ubuntu.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="border border-amber-400/40 bg-amber-400/5 p-10 text-center"
        data-testid="ubuntu-success"
      >
        <CheckCircle2 className="h-10 w-10 text-amber-400 mx-auto" />
        <h2 className="tok-headline-md mt-4">You&apos;re held.</h2>
        <p className="mt-3 text-zinc-300 max-w-xl mx-auto">
          The TOK Ubuntu team at{" "}
          <span className="text-amber-400">info@templeofkulture.co.uk</span>{" "}
          will reach out within five working days with the next retreat date,
          ticket / membership options and accessibility info.
        </p>
      </motion.div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="space-y-7"
      data-testid="ubuntu-form"
      noValidate
    >
      <div className="grid sm:grid-cols-2 gap-5">
        <Labelled label="Your name *">
          <input
            type="text"
            required
            value={form.name}
            onChange={set("name")}
            data-testid="ubuntu-name"
            className={INPUT}
          />
        </Labelled>
        <Labelled label="Email *">
          <input
            type="email"
            required
            value={form.email}
            onChange={set("email")}
            data-testid="ubuntu-email"
            className={INPUT}
          />
        </Labelled>
        <Labelled label="Phone (optional)">
          <input
            type="tel"
            value={form.phone}
            onChange={set("phone")}
            data-testid="ubuntu-phone"
            className={INPUT}
          />
        </Labelled>
        <Labelled label="Party size">
          <input
            type="number"
            min={1}
            max={80}
            value={form.party_size}
            onChange={set("party_size")}
            data-testid="ubuntu-party-size"
            className={INPUT}
          />
        </Labelled>
        <Labelled label="Preferred month (optional)">
          <input
            type="text"
            value={form.preferred_month}
            onChange={set("preferred_month")}
            placeholder="e.g. October 2026"
            data-testid="ubuntu-preferred-month"
            className={INPUT}
          />
        </Labelled>
      </div>

      <Labelled label="Who is this for?">
        <div className="flex flex-wrap gap-2" data-testid="ubuntu-audiences">
          {AUDIENCES.map((a) => (
            <button
              type="button"
              key={a.value}
              onClick={() =>
                setForm((f) => ({ ...f, audience_type: a.value }))
              }
              data-testid={`ubuntu-audience-${a.value}`}
              className={`px-3.5 py-2.5 text-[11px] uppercase tracking-[0.18em] font-bold border transition-colors ${
                form.audience_type === a.value
                  ? "bg-amber-400 text-black border-amber-400"
                  : "border-white/10 hover:border-amber-400 hover:text-amber-400 text-zinc-300"
              }`}
            >
              {a.label}
            </button>
          ))}
        </div>
      </Labelled>

      <MultiInterestSelector
        label="What's calling you in?"
        options={INTEREST_OPTIONS}
        value={form.interests}
        onChange={(interests) => setForm((f) => ({ ...f, interests }))}
        testIdPrefix="ubuntu-interest"
      />

      <ReferralSourceSelector
        value={form.referral_source}
        onChange={(v) => setForm((f) => ({ ...f, referral_source: v }))}
        testIdPrefix="ubuntu-referral"
      />

      <Labelled label="Accessibility / wellbeing notes (optional)">
        <textarea
          rows={3}
          value={form.accessibility_notes}
          onChange={set("accessibility_notes")}
          data-testid="ubuntu-accessibility"
          placeholder="Anything that would help us hold the space well for you."
          className={INPUT}
        />
      </Labelled>

      <Labelled label="Anything else?">
        <textarea
          rows={3}
          value={form.message}
          onChange={set("message")}
          data-testid="ubuntu-message"
          className={INPUT}
        />
      </Labelled>

      <button
        type="submit"
        disabled={submitting}
        data-testid="ubuntu-submit"
        className="tok-cta-label inline-flex items-center gap-3 bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-black px-8 py-4 transition-colors"
      >
        {submitting ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Sparkles className="h-4 w-4" />
        )}
        {submitting ? "Sending…" : "Reserve / register interest"}
      </button>
      <p className="text-xs text-zinc-500">
        Submissions are reviewed by the TOK team at{" "}
        <span className="text-amber-400">info@templeofkulture.co.uk</span>.
        Card, Apple Pay and PayPal (coming soon) accepted at checkout once a
        retreat is confirmed.
      </p>
    </form>
  );
}

const INPUT =
  "w-full bg-black border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-amber-400 transition-colors";

function Labelled({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.32em] font-bold text-zinc-400">
        {label}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
